import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { CheckCircle, XCircle, Clock, Square, Plug } from 'lucide-react';
import { gamingAPI } from '../services/api';
import { fetchBootProgress, floorBootView } from '../utils/bootProgressQuery';

const VMStatusDashboard = ({ session, onStatusUpdate }) => {
  const [vmStatus, setVmStatus] = useState({
    status: 'unknown',
    progress: 0,
    currentStep: 'Checking GPU instance status...',
    services: {
      parsec: false,
      nvidia_cuda: false,
      xfce4: false,
      steam: false,
      discord: false
    },
    error: null,
    status_messages: [],
    warnings: [],
    timeout_detected: false,
    vm_support_error: false
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [showTimeoutOptions, setShowTimeoutOptions] = useState(false);
  const instanceId = session?.instance_id || session?.vast_instance_id;

  const bootId = session?.db_id || instanceId;
  const { data: boot } = useQuery(
    ['bootProgress', bootId],
    () => fetchBootProgress(bootId),
    {
      enabled: !!bootId,
      staleTime: 0,
      keepPreviousData: true,
      refetchInterval: (latest) => (latest?.ready ? 8000 : 2000),
      refetchIntervalInBackground: true,
    }
  );

  const { data: play } = useQuery(
    ['playConnect', instanceId, 'wolf'],
    () => gamingAPI.getPlayConnect(instanceId, 'wolf'),
    {
      enabled: !!instanceId,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: true,
      refetchInterval: (latest) => {
        if (latest?.iscsi?.enabled && !latest.iscsi.ready) return 2000;
        if (latest?.tailscale_ip && latest?.public_ip) return 8000;
        if (latest?.public_ip) return 4000;
        return 2000;
      },
    }
  );

  const { data: gpuStatus } = useQuery(
    ['gpuStatus', instanceId],
    async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_BASE_URL}/vm-gaming/status/${instanceId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('status');
      return response.json();
    },
    {
      enabled: !!instanceId && !['running', 'success'].includes(String(session?.status || '')),
      staleTime: 0,
      refetchInterval: 15000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
    }
  );

  const handleInstanceAction = async (action) => {
    try {
      const response = await fetch(`/api/v1/vm-gaming/sessions/${session.instance_id}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ action })
      });

      if (response.ok) {
        const data = await response.json();
        if (action === 'terminate') {
          // Refresh the page or redirect to GPU selection
          window.location.reload();
        } else {
          setShowTimeoutOptions(false);
        }
        alert(data.message);
      } else {
        throw new Error('Failed to handle action');
      }
    } catch (error) {
      console.error('Error handling instance action:', error);
      alert('Failed to handle action. Please try again.');
    }
  };

  useEffect(() => {
    if (!gpuStatus) return;
    setVmStatus((prev) => ({
      ...prev,
      status: gpuStatus.status || 'unknown',
      progress: gpuStatus.progress || 0,
      currentStep: gpuStatus.current_step || 'Initializing...',
      services: gpuStatus.services || prev.services,
      error: gpuStatus.error || null,
      status_messages: gpuStatus.status_messages || [],
      warnings: gpuStatus.warnings || [],
      timeout_detected: gpuStatus.timeout_detected || false,
      vm_support_error: gpuStatus.vm_support_error || false,
    }));
    if (gpuStatus.timeout_detected) setShowTimeoutOptions(true);
    if (onStatusUpdate) {
      onStatusUpdate({
        status: gpuStatus.status,
        is_ready: gpuStatus.status === 'ready',
        services: gpuStatus.services,
      });
    }
  }, [gpuStatus, onStatusUpdate]);

  const getStatusIcon = (status, vmSupportError) => {
    if (vmSupportError) {
      return <XCircle className="h-5 w-5 text-red-400" />;
    }
    switch (status) {
      case 'ready': return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'deploying': return <Clock className="h-5 w-5 text-yellow-400" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-400" />;
      case 'stopped': return <Square className="h-5 w-5 text-gray-400" />;
      default: return <Clock className="h-5 w-5 text-yellow-400" />;
    }
  };

  const getStatusColor = (status, vmSupportError) => {
    if (vmSupportError) {
      return 'bg-red-500';
    }
    switch (status) {
      case 'ready': return 'bg-green-500';
      case 'deploying': return 'bg-yellow-500';
      case 'failed': return 'bg-red-500';
      case 'stopped': return 'bg-gray-500';
      default: return 'bg-yellow-500';
    }
  };

  const getStatusText = (status, vmSupportError) => {
    if (vmSupportError) {
      return 'GPU INSTANCE ERROR';
    }
    switch (status) {
      case 'ready': return 'GPU - READY';
      case 'deploying': return 'DEPLOYING';
      case 'failed': return 'FAILED';
      case 'stopped': return 'STOPPED';
      default: return 'UNKNOWN';
    }
  };

  const getServiceIcon = (serviceName, isReady) => {
    if (isReady) {
      return <CheckCircle className="h-4 w-4 text-green-400" />;
    }
    return <Clock className="h-4 w-4 text-yellow-400" />;
  };

  if (!session) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">GPU Instance Status</h3>
          <span className="text-gray-400">No active session</span>
        </div>
      </div>
    );
  }

  const gpuUp =
    vmStatus.status === 'ready' ||
    session?.status === 'running' ||
    session?.status === 'success';
  const bootFailed = !!boot?.failed || session?.status === 'failed';
  const streamReady = !!(play?.heal_ready || play?.play_ready);
  const playable = gpuUp && streamReady && !bootFailed;
  const isReady = playable;
  const apiPct = parseInt(boot?.percent, 10);
  const localPct = isReady
    ? 100
    : playable
      ? 92
      : 0;
  const floored = floorBootView(bootId, boot, localPct);
  const bootPct = bootFailed
    ? Math.min(20, Number.isFinite(apiPct) ? apiPct : 8)
    : floored.percent;
  const waitLabel = bootFailed
    ? 'GPU did not start — pick another'
    : isReady
    ? 'Machine Ready'
    : playable
      ? 'Ready to play in the browser'
      : floored.current
        ? `${floored.current} — ${bootPct}%`
        : `Starting GPU… — ${bootPct}%`;
  const stepPct = isReady ? 100 : bootPct;

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      {/* Simple Status Button */}
      <div className="flex flex-col items-center gap-3">
        <button
          className={`px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 flex items-center space-x-2 ${
            bootFailed
              ? 'bg-red-600'
              : isReady
              ? 'bg-green-500 hover:bg-green-600'
              : playable
                ? 'bg-emerald-600 hover:bg-emerald-500'
                : 'bg-yellow-500 hover:bg-yellow-600'
          }`}
          disabled={!playable}
        >
          {playable ? (
            <>
              <CheckCircle className="h-5 w-5" />
              <span>{waitLabel}</span>
            </>
          ) : bootFailed ? (
            <>
              <XCircle className="h-5 w-5" />
              <span>{waitLabel}</span>
            </>
          ) : (
            <>
              <Clock className="h-5 w-5" />
              <span>{waitLabel}</span>
            </>
          )}
        </button>
        <div className="w-full max-w-md">
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                bootFailed ? 'bg-red-400' : isReady ? 'bg-emerald-400' : 'bg-amber-400'
              }`}
              style={{ width: `${stepPct}%` }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-white/60">
            {bootFailed
              ? (boot?.detail || 'The rented host failed. We closed that GPU so you can pick another.')
              : isReady
              ? 'GPU, browser stream, and Tailscale are all up.'
              : playable
                ? 'Browser play is available. Tailscale 100.x will appear when the VPN joins — no refresh needed.'
                : 'Updates every second — you do not need to refresh the page.'}
          </p>
        </div>
        {play?.iscsi?.enabled && (
          <div className="w-full max-w-md mt-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs text-white/70">NAS library</p>
              <p className="text-xs font-mono text-white/60">
                {play.iscsi.ready ? 'Ready' : `${Math.max(0, Number(play.iscsi.percent) || 0)}%`}
              </p>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10 mt-1">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  play.iscsi.status === 'error' ? 'bg-red-400' : play.iscsi.ready ? 'bg-emerald-400' : 'bg-sky-400'
                }`}
                style={{
                  width: `${play.iscsi.ready ? 100 : Math.max(6, Math.min(100, Number(play.iscsi.percent) || 0))}%`,
                }}
              />
            </div>
            <p className="mt-1 text-center text-[11px] text-white/50">
              {play.iscsi.label || 'Preparing extra disk…'}
            </p>
          </div>
        )}
      </div>


      {/* Error Display */}
      {vmStatus.vm_support_error && (
        <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="h-5 w-5 text-red-400" />
            <span className="text-red-400 font-semibold">GPU Instance Error</span>
          </div>
          <p className="text-sm text-red-300 mb-2">
            This GPU instance does not support containers. The instance has been automatically terminated to prevent charges.
          </p>
          <p className="text-xs text-red-400">
            Please select a different GPU that supports virtualization.
          </p>
        </div>
      )}

      {/* Error Display */}
      {vmStatus.error && !vmStatus.vm_support_error && (
        <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-red-400" />
            <span className="text-sm text-red-400">Error: {vmStatus.error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VMStatusDashboard;

